import React, { useEffect, useState } from "react";
import {
  Check,
  CheckCircle,
  XCircle,
  CircleCheck,
  CircleMinus,
  Edit2,
  Lock,
  Link as LinkIcon,
} from "lucide-react";
import Modal from "../common/Modal";
import Button from "../common/Button";
import FormControl from "../common/FormControl";
import NoDataFound from "../common/NoDataFound";
import Toast, { ToastMessage } from "../common/Toast";
import {
  DualListItem,
} from "../audits/procedure-types/common/DualListSelector";
import linkService, {
  LinkedItem,
  RiskLinkType,
} from "../../services/linkService";
import riskCausesService from "../../services/riskCausesService";
import riskImpactsService from "../../services/riskImpactsService";
import controlRegisterService from "../../services/controlRegisterService";
import riskRegisterService, {
  RiskCategory,
} from "../../services/riskRegisterService";
import RiskVisualization from "./RiskVisualization";
import extractApiError from "../../utils/apiError";
import Skeleton from "../common/Skeleton";
import { confirmWarning, confirmActive, swalError } from "../common/confirm";
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedTextarea } from '../../validation/ValidatedFields';

type TabKey = "edit" | RiskLinkType | "canvas";

const ALL_TABS: { key: TabKey; label: string }[] = [
  { key: "edit", label: "Basic Details" },
  { key: "canvas", label: "Canvas" },
  { key: "causes", label: "Causes" },
  { key: "impacts", label: "Impacts" },
  { key: "controls", label: "Controls" },
];

type RiskLite = {
  id: number;
  /** System-generated code (shown in the list); read-only in the edit form. */
  code?: string | null;
  risk_code: string;
  risk_name: string;
  description?: string;
  category_id?: number;
  status?: string;
  is_active?: number;
};

const RiskConfigModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  risk: RiskLite;
  initialTab?: TabKey;
  onSaved?: () => void;
  canModify?: boolean;
  canApprove?: boolean;
}> = ({ isOpen, onClose, risk, initialTab, onSaved, canModify = false, canApprove = false }) => {
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab || "canvas");
  useEffect(() => {
    if (isOpen) setActiveTab(initialTab || "canvas");
  }, [isOpen, initialTab]);

  // ---- Edit tab state ----
  const [formData, setFormData] = useState({
    risk_code: risk.risk_code,
    risk_name: risk.risk_name,
    description: risk.description || "",
    category_id: risk.category_id,
  });
  const [localStatus, setLocalStatus] = useState(risk.status || "DRAFT");
  // Active flag is independent of workflow_state: a risk in ANY state can be
  // toggled active/inactive. Drive the toggle button off this, not localStatus.
  const [localActive, setLocalActive] = useState(risk.is_active !== 0);
  const [categories, setCategories] = useState<RiskCategory[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setFormData({
      risk_code: risk.risk_code,
      risk_name: risk.risk_name,
      description: risk.description || "",
      category_id: risk.category_id,
    });
    setLocalStatus(risk.status || "DRAFT");
    setIsEditMode(false);
  }, [isOpen, risk]);

  useEffect(() => {
    if (!isOpen) return;
    riskRegisterService
      .getActiveCategories()
      .then(setCategories)
      .catch(() => { });
  }, [isOpen]);

  // ---- Link tabs state ----
  const [available, setAvailable] = useState<DualListItem[]>([]);
  const [selected, setSelected] = useState<DualListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<DualListItem | null>(null);
  const [originalEditingItem, setOriginalEditingItem] = useState<DualListItem | null>(null);
  const [editingItemDescription, setEditingItemDescription] = useState("");
  const [editingSubmitting, setEditingSubmitting] = useState(false);
  const linkDescVh = useFieldValidation();

  useEffect(() => {
    // Only the dual-list link tabs load here. "edit" and "canvas" manage their
    // own data ("canvas" is the bow-tie viz) and "canvas" is not a valid link
    // type, so calling listForRisk with it returns a non-array → crash.
    if (!isOpen || (activeTab !== "causes" && activeTab !== "impacts" && activeTab !== "controls")) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setToast(null);
      try {
        let allItems: DualListItem[] = [];
        if (activeTab === "causes") {
          // Dropdown endpoint → approved + active causes only.
          const all = await riskCausesService.getDropdown();
          allItems = all.map((c) => ({
            id: c.id,
            title: c.name,
          }));
        } else if (activeTab === "impacts") {
          // Dropdown endpoint → approved + active impacts only.
          const all = await riskImpactsService.getDropdown();
          allItems = all.map((i) => ({
            id: i.id,
            title: i.name,
          }));
        } else {
          // Dropdown endpoint → approved + active controls only.
          const all = await controlRegisterService.getDropdown();
          allItems = all.map((c) => ({
            id: c.id,
            title: c.code || c.name,
            subtitle: c.control_type ?? undefined,
            description: c.name,
            category: c.category_name ?? undefined,
          }));
        }
        const linked: LinkedItem[] = await linkService.listForRisk(
          risk.id,
          activeTab as RiskLinkType,
        );
        const linkedItems: DualListItem[] = linked.map((l) => ({
          id: l.id,
          title: l.code || l.name,
          description: l.code ? l.name : l.description,
        }));
        if (cancelled) return;
        setAvailable(allItems);
        setSelected(linkedItems);
      } catch (e: any) {
        if (!cancelled)
          setToast({
            type: "error",
            message: extractApiError(e, "Failed to load"),
          });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, activeTab, risk.id]);

  const handleSaveLinks = async () => {
    setSaving(true);
    setToast(null);
    try {
      await linkService.replaceForRisk(
        risk.id,
        activeTab as RiskLinkType,
        selected.map((s) => Number(s.id)),
      );

      setToast({
        type: "success",
        message: `Saved. ${selected.length} ${activeTab} linked.`,
      });
    } catch (e: any) {
      setToast({ type: "error", message: extractApiError(e, "Save failed") });
    } finally {
      setSaving(false);
    }
  };

  const handleAddItem = async () => {
    if (selectedItemId === null) return;
    const item = available.find((a) => a.id === selectedItemId);
    if (!item) return;
    const alreadyAdded = selected.some((s) => s.id === selectedItemId);
    if (alreadyAdded) {
      setToast({ type: "error", message: `${item.title} is already linked.` });
      return;
    }

    const newSelected = [...selected, item];
    setSelected(newSelected);
    setShowAddModal(false);
    setSelectedItemId(null);

    // Save the link to backend
    setSaving(true);
    setToast(null);
    try {
      await linkService.replaceForRisk(
        risk.id,
        activeTab as RiskLinkType,
        newSelected.map((s) => Number(s.id)),
      );
      setToast({
        type: "success",
        message: `${item.title} linked successfully.`,
      });
    } catch (e: any) {
      setSelected(selected); // Revert on error
      setToast({
        type: "error",
        message: extractApiError(e, "Failed to link item"),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEditItem = (item: DualListItem) => {
    setOriginalEditingItem(item);
    setEditingItem(item);
    setEditingItemDescription(item.description || "");
    linkDescVh.reset();
    setShowEditModal(true);
  };


  const handleUpdateEditingItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem) return;
    const valid = linkDescVh.validateAll([
      { name: 'description', code: 110, value: editingItemDescription, required: false },
    ]);
    if (!valid) return;
    setEditingSubmitting(true);
    setToast(null);
    try {
      if (activeTab === "causes") {
        await riskCausesService.update(editingItem.id as number, {
          description: editingItemDescription,
        });
      } else if (activeTab === "impacts") {
        await riskImpactsService.update(editingItem.id as number, {
          impact_description: editingItemDescription,
        });
      } else if (activeTab === "controls") {
        await controlRegisterService.update(editingItem.id as number, {
          control_description: editingItemDescription,
        });
      }

      const updatedItem = {
        ...editingItem,
        description: editingItemDescription,
      };
      setSelected(
        selected.map((item) =>
          item.id === editingItem.id ? updatedItem : item,
        ),
      );
      setOriginalEditingItem(updatedItem);
      setToast({ type: "success", message: "Item updated successfully." });
      setShowEditModal(false);
      setEditingItem(null);
      setOriginalEditingItem(null);
    } catch (e: any) {
      setToast({
        type: "error",
        message: extractApiError(e, "Update failed"),
      });
    } finally {
      setEditingSubmitting(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setToast(null);
    try {
      await riskRegisterService.update(risk.id, {
        risk_code: formData.risk_code,
        risk_name: formData.risk_name,
        description: formData.description,
        category_id: formData.category_id,
      });
      onSaved?.();
      setToast({ type: "success", message: "Risk updated successfully." });
      setIsEditMode(false);
    } catch (e: any) {
      setToast({ type: "error", message: extractApiError(e, "Update failed") });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async () => {
    const goingInactive = localActive;   // currently active -> will inactivate
    const confirmFn = goingInactive ? confirmWarning : confirmActive;
    const ok = await confirmFn({
      title: goingInactive ? "Inactivate this risk?" : "Reactivate this risk?",
      text: goingInactive
        ? "It will be hidden from active lists."
        : "It will appear in active lists again.",
      confirmText: goingInactive ? "Yes, Inactivate" : "Yes, Reactivate",
    });
    if (!ok) return;
    setToggling(true);
    try {
      await riskRegisterService.toggleStatus(risk.id);
      setLocalActive(!goingInactive);
      // Keep the workflow-state label in sync for approved rows (APPROVED<->INACTIVE).
      if (localStatus === "APPROVED" || localStatus === "INACTIVE") {
        setLocalStatus(goingInactive ? "INACTIVE" : "APPROVED");
      }
      onSaved?.();
      setToast({
        type: goingInactive ? "warning" : "success",
        message: goingInactive ? "Risk Inactivated." : "Risk Reactivated.",
      });
    } catch (e: any) {
      setToast({ type: "error", message: extractApiError(e, "Toggle failed") });
    } finally {
      setToggling(false);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      await riskRegisterService.approve(risk.id);
      setLocalStatus("APPROVED");
      onSaved?.();
      setToast({ type: "success", message: "Risk approved." });
    } catch (e: any) {
      setToast({
        type: "error",
        message: extractApiError(e, "Approve failed"),
      });
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    setRejecting(true);
    try {
      await riskRegisterService.reject(risk.id, '');
      setLocalStatus("REJECTED");
      onSaved?.();
      setToast({ type: "success", message: "Risk rejected." });
    } catch (e: any) {
      setToast({ type: "error", message: extractApiError(e, "Reject failed") });
    } finally {
      setRejecting(false);
    }
  };

  const anyBusy =
    submitting ||
    toggling ||
    approving ||
    rejecting ||
    saving ||
    editingSubmitting;
  // Approved records are frozen: no fields, no toggle, no edit. Unlock via the
  // status dot's Reopen action.
  const isApproved = localStatus === "APPROVED";
  const showToggle = !isApproved;   // toggle active/inactive allowed in any non-approved state
  const showApproveReject = localStatus === "DRAFT";
  const categoryOptions = categories.map((c) => ({
    value: c.id,
    label: c.category_name,
  }));

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
        closeOnOverlayClick={false}
        contentClassName="app-modal-content--with-table"
      >
        <Modal.Header>
          {activeTab === "edit" && isApproved ? (
            <>
              Viewing (locked) "<span className="item-highlight">{risk.risk_name}</span>"
            </>
          ) : activeTab === "edit" && isEditMode ? (
            <>
              Editing "<span className="item-highlight">{risk.risk_name}</span>"
            </>
          ) : (
            <>
              Risk Configuration — {risk.risk_name}
            </>
          )}
        </Modal.Header>

        <Modal.Body className="">
          <div className="app-modal-tabs">
            {ALL_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                className={`app-modal-tab${activeTab === t.key ? " app-modal-tab--active" : ""}`}
                data-testid={`TAB_risk_config_${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {activeTab === "canvas" ? (
            <div
              className="!p-0 overflow-hidden"
              style={{ margin: "0 -24px -24px" }}
            >
              <RiskVisualization risk={risk} canModify={canModify} />
            </div>
          ) : activeTab === "edit" ? (
            <form
              id="risk-config-edit-form"
              onSubmit={handleUpdate}
              className="flex flex-col gap-y-4 pt-4"
              data-testid="MOD_risk_config_edit_form"
            >
              {isApproved && (
                <div className="approved-banner" data-testid="BANNER_risk_config_locked">
                  <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
                </div>
              )}
              {/* System code (shown in the list) — read-only, never editable. */}
              <FormControl.Input
                label="Code"
                value={risk.code || '—'}
                disabled
                data-testid="INP_risk_config_edit_code"
              />
              <FormControl.Input
                label="Risk Name"
                value={formData.risk_name}
                onChange={(e) =>
                  setFormData({ ...formData, risk_name: e.target.value })
                }
                disabled={!isEditMode}
                required
                data-testid="INP_risk_config_edit_name"
              />
              <FormControl.SearchSelect
                label="Category"
                options={categoryOptions}
                value={formData.category_id ?? null}
                onChange={(v) =>
                  setFormData({
                    ...formData,
                    category_id: v !== null ? Number(v) : undefined,
                  })
                }
                placeholder="— Select category —"
                disabled={!isEditMode}
                clearable
                data-testid="SEL_risk_config_edit_category"
              />
              <FormControl.Textarea
                label="Description"
                value={formData.description || ""}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                disabled={!isEditMode}
                data-testid="INP_risk_config_edit_description"
              />
            </form>
          ) : (
            <div className="flex flex-col h-full">
              <div className="netbody-header">
                <div className="flex items-center gap-2 ml-auto">
                  <Button
                    variant="fill"
                    icon={LinkIcon}
                    onClick={() => {
                      if (isApproved) { setToast({ type: 'error', message: 'Record is approved and locked. Reopen via the workflow button to edit.' }); return; }
                      if (!canModify) { swalError('You do not have permission to modify this record.'); return; }
                      setShowAddModal(true);
                    }}
                    disabled={loading}
                    className={(!canModify || isApproved) ? 'opacity-50 cursor-not-allowed' : ''}
                    data-testid="BTN_risk_config_link_add"
                  >
                    Link
                  </Button>
                </div>
              </div>

              <div className="table-list flex-1 min-h-0">
                <div className="table-list-scroll">
                  <table className="table-list-table">
                    <thead>
                      <tr>
                        <th className="table-list-th table-list-cell--sno">S.No</th>
                        <th className="table-list-th">
                          {activeTab === "controls"
                            ? "Control Code"
                            : activeTab === "causes"
                              ? "Cause Name"
                              : "Impact Name"}
                        </th>
                        <th className="table-list-th">Description</th>
                      </tr>
                    </thead>
                    <tbody aria-busy={loading} aria-live="polite">
                      {loading && <Skeleton.TableRows cols={['3xs', 'md', 'md']} />}
                      {!loading && selected.length === 0 && (
                        <tr>
                          <td colSpan={3} className="table-list-cell">
                            <NoDataFound
                              title="Nothing Linked"
                              message={`No ${activeTab} linked. Click "Link" to add one.`}
                            />
                          </td>
                        </tr>
                      )}
                      {!loading &&
                        selected.map((item, idx) => (
                          <tr
                            key={item.id}
                            className="table-list-row"
                          >
                            <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                            <td className="table-list-cell">
                              <span className="table-list-cell-title">
                                {item.title}
                              </span>
                            </td>
                            <td
                              className="table-list-cell table-list-cell--truncate"
                              title={item.description || ""}
                            >
                              {item.description || "—"}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>

        {activeTab !== "canvas" && (
          <Modal.Footer
            className={
              activeTab === "edit" && showToggle ? "app-modal-footer--split" : ""
            }
          >
            {activeTab === "edit" && !isApproved ? (
              <>
                {showToggle && (
                  <Button
                    variant={localActive ? "inactive-outline" : "active-outline"}
                    icon={localActive ? CircleMinus : CircleCheck}
                    onClick={() => { if (!canApprove) { swalError('You do not have permission to approve this record.'); return; } handleToggle(); }}
                    disabled={anyBusy}
                    className={!canApprove ? 'opacity-50 cursor-not-allowed' : ''}
                    data-testid="BTN_risk_config_edit_toggle"
                  >
                    {toggling ? "..." : localActive ? "Inactive" : "Active"}
                  </Button>
                )}
                <div className="flex gap-2">
                  {isEditMode ? (
                    <>
                      {showApproveReject && (
                        <>
                          <Button
                            variant="delete-outline"
                            icon={XCircle}
                            onClick={() => { if (!canApprove) { swalError('You do not have permission to approve this record.'); return; } handleReject(); }}
                            disabled={anyBusy}
                            className={!canApprove ? 'opacity-50 cursor-not-allowed' : ''}
                            data-testid="BTN_risk_config_edit_reject"
                          >
                            {rejecting ? "Rejecting..." : "Reject"}
                          </Button>
                          <Button
                            variant="update-outline"
                            icon={CheckCircle}
                            onClick={() => { if (!canApprove) { swalError('You do not have permission to approve this record.'); return; } handleApprove(); }}
                            disabled={anyBusy}
                            className={!canApprove ? 'opacity-50 cursor-not-allowed' : ''}
                            data-testid="BTN_risk_config_edit_approve"
                          >
                            {approving ? "Approving..." : "Approve"}
                          </Button>
                        </>
                      )}
                      <Button
                        type="submit"
                        form="risk-config-edit-form"
                        variant="update"
                        icon={Check}
                        disabled={anyBusy}
                        data-testid="BTN_risk_config_edit_update"
                      >
                        {submitting ? "Updating..." : "Update"}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="fill"
                      icon={Edit2}
                      onClick={() => { if (!canModify) { swalError('You do not have permission to modify this record.'); return; } setIsEditMode(true); }}
                      disabled={anyBusy}
                      className={!canModify ? 'opacity-50 cursor-not-allowed' : ''}
                      data-testid="BTN_risk_config_edit_enter"
                    >
                      Edit
                    </Button>
                  )}
                </div>
              </>
            ) : null}
          </Modal.Footer>
        )}

        {activeTab !== "edit" && activeTab !== "canvas" && (
          <Modal
            isOpen={showAddModal}
            onClose={() => {
              setShowAddModal(false);
              setSelectedItemId(null);
            }}
            size="sm"
          >
            <Modal.Header>Link {activeTab}</Modal.Header>
            <Modal.Body>
              <form
                id="add-link-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleAddItem();
                }}
                className="flex flex-col gap-y-4"
                data-testid="MOD_risk_config_link_form"
              >
                <FormControl.SearchSelect
                  label={`Select ${activeTab}`}
                  options={available.map((a) => ({
                    value: a.id,
                    label: a.title,
                  }))}
                  value={selectedItemId}
                  onChange={(v) =>
                    setSelectedItemId(v !== null ? Number(v) : null)
                  }
                  placeholder={`— Choose ${activeTab} —`}
                  required
                  data-testid="SEL_risk_config_link_item"
                />
              </form>
            </Modal.Body>
            <Modal.Footer>
              <Button
                type="submit"
                form="add-link-form"
                variant="fill"
                icon={Check}
                disabled={selectedItemId === null || saving}
                data-testid="BTN_risk_config_link_submit"
              >
                {saving ? 'Submitting...' : 'Submit'}
              </Button>
            </Modal.Footer>
          </Modal>
        )}

        {activeTab !== "edit" && activeTab !== "canvas" && (
          <Modal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setEditingItem(null);
            }}
            size="md"
          >
            <Modal.Header>
              {originalEditingItem ? (
                <>
                  Editing "<span className="item-highlight">{originalEditingItem.title}</span>"
                </>
              ) : (
                `Add ${activeTab}`
              )}
            </Modal.Header>
            <Modal.Body>
              <form
                id="edit-link-item-form"
                onSubmit={handleUpdateEditingItem}
                className="flex flex-col gap-y-4"
                data-testid="MOD_risk_config_link_edit_form"
              >
                <FormControl.SearchSelect
                  label={activeTab === "controls" ? "Code" : "Name"}
                  options={available.map((a) => ({
                    value: a.id,
                    label: a.title,
                  }))}
                  value={editingItem?.id || null}
                  onChange={(v) => {
                    const item = available.find((a) => a.id === v);
                    if (item) setEditingItem(item);
                  }}
                  placeholder={`— Select ${activeTab} —`}
                  required
                  data-testid="SEL_risk_config_link_edit_item"
                />
                <ValidatedTextarea
                  name="description"
                  code={110}
                  label="Description"
                  value={editingItemDescription}
                  onChange={(e) => setEditingItemDescription(e.target.value)}
                  vh={linkDescVh}
                  data-testid="INP_risk_config_link_edit_description"
                />
              </form>
            </Modal.Body>
            <Modal.Footer>
              <Button
                type="submit"
                form="edit-link-item-form"
                variant="update"
                icon={Check}
                disabled={editingSubmitting}
                data-testid="BTN_risk_config_link_edit_update"
              >
                {editingSubmitting ? "Updating..." : "Update"}
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </Modal>
      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
};

export default RiskConfigModal;
